import { useEffect, useState } from 'react';

import { fetchMyTeam } from '../services/publicRuns';

export type JoinDestination =
  | { kind: 'loading' }
  | { kind: 'no-team' }
  | { kind: 'team-pending'; runId: string }
  | { kind: 'team-ready'; runId: string };

/**
 * Resolve where to send an authenticated user landing on the join wizard.
 *
 * - `no-team`     → user has no team in this run yet → continue with FindTeam.
 * - `team-pending`→ user is in a team that hasn't picked a name → continue
 *                   with the team-flow waiting screens (let WS push the
 *                   transition).
 * - `team-ready`  → user is in a fully-named team → skip the wizard and go
 *                   directly to /runs/:runId/participant/stations.
 *
 * Errors / 401 / 404 fall through to `no-team` so the caller stays on the
 * normal flow rather than getting blocked by a transient backend issue.
 */
export function useResolveJoinDestination(code: string | undefined): JoinDestination {
  const [state, setState] = useState<JoinDestination>(
    code ? { kind: 'loading' } : { kind: 'no-team' },
  );

  useEffect(() => {
    if (!code) {
      setState({ kind: 'no-team' });
      return;
    }
    const controller = new AbortController();
    setState({ kind: 'loading' });
    fetchMyTeam(code, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      if (result.kind !== 'ok' || result.data.team_id === null) {
        setState({ kind: 'no-team' });
        return;
      }
      // RunShell + TeamInfo read team_id from sessionStorage to load the
      // team detail (the key was originally set only by the guest-join
      // flow). Persist it here so the share-code chip and team header
      // render for the authed-user direct-to-run path too.
      try {
        sessionStorage.setItem(
          `clavis_team_for_run_${result.data.run_id}`,
          result.data.team_id,
        );
      } catch {
        // Storage disabled (private mode etc) — non-fatal; the chip just
        // won't show until a hard reload after sessionStorage is available.
      }
      if (result.data.team_name === null) {
        setState({ kind: 'team-pending', runId: result.data.run_id });
      } else {
        setState({ kind: 'team-ready', runId: result.data.run_id });
      }
    });
    return () => controller.abort();
  }, [code]);

  return state;
}
